import { Theme } from '@emotion/react';
import { CameraAlt, Close, QrCode } from '@mui/icons-material';
import {
  Divider,
  css,
  Dialog,
  DialogTitle,
  DialogContent,
  AvatarGroup,
  Typography,
  Box,
  DialogActions,
  IconButton,
  Skeleton,
  Button,
} from '@mui/material';
import { useCommunityPointsPerActions } from 'admin-features/settings/hooks';
import { Fragment, useMemo } from 'react';

import { ATTENDANCE_WINDOW_HOURS_AFTER_EVENT } from '@jebel/constants';
import { isBetween, isFutureDate, isPastDate, parseDate } from '@jebel/utils';

import { OrganizationAvatar } from 'shared/components/symbols';
import { LoadingOverlay, MediaGallery } from 'shared/components/ui';
import { StreamActivityProvider, useStreamChatEnrichedActivity } from 'shared/features/posts';
import { EventInfoFragment } from 'shared/graphql';
import {
  useCurrentUser,
  useCurrentUserPermissions,
  useModalState,
  useStreamChatReactions,
  useRelativeDate,
} from 'shared/hooks';
import { formatTimeAgo } from 'shared/utils/date';

import { EventAttendanceModal } from './EventAttendanceModal';
import { EventCodeModal } from './EventCodeModal';
import { EventComments } from './EventComments';
import { EventInfoModalDateLocation } from './EventInfoModalDateLocation';

export type EventInfoModalProps = {
  event: EventInfoFragment;

  show: boolean;

  onClose?: () => void;
};

export function EventDetailsModal({ event, show, onClose }: EventInfoModalProps) {
  const { user } = useCurrentUser();
  const { isCommunityAdministrator } = useCurrentUserPermissions();
  const { data: pointsPerActions } = useCommunityPointsPerActions();

  const { data: activity, loading: isActivityLoading } = useStreamChatEnrichedActivity({
    post: event,
  });

  const {
    show: showAttendanceModal,
    open: openAttendanceModal,
    close: closeAttendanceModal,
  } = useModalState();

  const {
    show: showEventCodeModal,
    open: openEventCodeModal,
    close: closeEventCodeModal,
  } = useModalState();

  const { onCommentAdd, onLikeAdd, onReactionRemove, onCommentRemove } = useStreamChatReactions();

  const images = event?.images?.items ?? [];
  const otherSponsors = event?.otherSponsors?.items ?? [];

  const openingDate = useMemo(() => {
    return event.openingDate ? parseDate(event.openingDate) : parseDate(event.date!);
  }, [event.date, event.openingDate]);

  const closingDate = useMemo(() => {
    return event.closingDate
      ? parseDate(event.closingDate)
      : parseDate(event.date!).plus({ hours: ATTENDANCE_WINDOW_HOURS_AFTER_EVENT });
  }, [event.date, event.closingDate]);

  const closingDateRelative = useRelativeDate(closingDate);

  const canShowAttendanceCode = useMemo(() => {
    const isAuthor = event.author && event.author?.id === user?.id;
    const isCommunityAdministrated = !event.author && isCommunityAdministrator;

    return isAuthor || isCommunityAdministrated;
  }, [event.author, user?.id, isCommunityAdministrator]);

  const canMarkAttendance = useMemo(() => {
    if (!openingDate || !closingDate) {
      return false;
    }

    return isBetween(new Date(), { start: openingDate, end: closingDate });
  }, [openingDate, closingDate]);

  const attendanceButtonLabel = useMemo(() => {
    const isEarly = isFutureDate(openingDate);

    if (isEarly) {
      const openingDateRelative = formatTimeAgo(openingDate);
      return `You can mark attendance ${openingDateRelative}`;
    }

    const isLate = isPastDate(closingDate);

    if (isLate) {
      return `You can no longer mark attendance.`;
    }

    return `Mark attendance (+${pointsPerActions?.EventAttended} points)`;
  }, [openingDate, closingDate, pointsPerActions]);

  return (
    <Dialog open={show} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle css={headerCSS}>
        <Typography variant="h6">{event.title}</Typography>

        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <StreamActivityProvider data={activity} loading={isActivityLoading}>
        <DialogContent css={contentCSS} dividers>
          <LoadingOverlay>
            <EventInfoModalDateLocation event={event!} />
          </LoadingOverlay>

          <Divider />

          <Box css={descriptionContainerCSS}>
            <Box>
              <Typography css={descriptionTitleCss} color="primary">
                Description
              </Typography>

              {event.description ? (
                <Typography variant="body1">{event.description}</Typography>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  No description available
                </Typography>
              )}
            </Box>

            {event?.titleSponsor && (
              <Box display="flex" flexDirection="column" alignItems="center" gap="0.5rem">
                <Typography css={titleSponsorCss} color="textSecondary">
                  Title Sponsor
                </Typography>

                <OrganizationAvatar
                  variant="rounded"
                  size={96}
                  organization={event?.titleSponsor}
                />
              </Box>
            )}
          </Box>

          <MediaGallery media={images} />

          {otherSponsors.length > 0 && (
            <Fragment>
              <Typography css={titleSponsorCss} color="textSecondary">
                Other Sponsors
              </Typography>

              <AvatarGroup max={4} total={otherSponsors.length}>
                {otherSponsors.map((sponsor, index) => (
                  <OrganizationAvatar key={index} organization={sponsor} />
                ))}
              </AvatarGroup>
            </Fragment>
          )}

          <Box css={buttonsCSS}>
            {canShowAttendanceCode && (
              <>
                <Button
                  disableElevation
                  variant="contained"
                  color="primary"
                  startIcon={<QrCode />}
                  css={{ flex: 1 }}
                  onClick={openEventCodeModal}
                >
                  Show attendance code
                </Button>
              </>
            )}

            <EventCodeModal
              event={event.id}
              show={showEventCodeModal}
              onClose={closeEventCodeModal}
            />

            <Button
              disableElevation
              variant="contained"
              color="secondary"
              startIcon={<CameraAlt />}
              disabled={!canMarkAttendance}
              css={{ flex: 1 }}
              onClick={openAttendanceModal}
            >
              {attendanceButtonLabel}
            </Button>

            <EventAttendanceModal show={showAttendanceModal} onClose={closeAttendanceModal} />
          </Box>

          {canMarkAttendance && (
            <Typography variant="caption" color="textSecondary">
              {canMarkAttendance ? 'Closing in' : 'Closed'} {closingDateRelative}
            </Typography>
          )}
        </DialogContent>

        <DialogActions css={commentsCSS}>
          <LoadingOverlay
            overlay={<Skeleton variant="rectangular" width="100%" height="5rem" />}
            loading={isActivityLoading}
          >
            <EventComments
              onCommentAdd={onCommentAdd}
              onLikeAdd={onLikeAdd}
              onReactionRemove={onReactionRemove}
              onCommentRemove={onCommentRemove}
              activity={activity}
            />
          </LoadingOverlay>
        </DialogActions>
      </StreamActivityProvider>
    </Dialog>
  );
}

const headerCSS = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const contentCSS = css`
  display: grid;
  grid-row-gap: 1rem;
`;

const descriptionContainerCSS = (theme: Theme) => css`
  display: grid;
  grid-template-columns: 4fr 1fr;
  gap: 1rem;

  ${theme.breakpoints.down('md')} {
    grid-template-columns: 1fr;
  }
`;

const descriptionTitleCss = css`
  font-weight: 500;
`;

const titleSponsorCss = (theme: Theme) => css`
  font-size: ${theme.typography.fontSize + 2}px;
  font-weight: 500;

  ${theme.breakpoints.down('md')} {
    font-size: ${theme.typography.fontSize}px;
  }
`;

const buttonsCSS = css`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const commentsCSS = css`
  display: flex;
  justify-items: start;
  align-items: stretch;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`;
